import { supabase } from '../lib/supabase'
import { Complaint, ComplaintCategory, ComplaintStatus, User as UserType } from '../types'

export interface AnalyticsData {
  totalComplaints: number
  resolvedComplaints: number
  pendingComplaints: number
  inProgressComplaints: number
  avgResolutionTime: number
  categoryData: { category: ComplaintCategory; count: number; resolved: number }[]
  monthlyData: { month: string; complaints: number; resolved: number }[]
  locationData: { lat: number; lng: number; count: number; category: ComplaintCategory }[]
  officialData: { name: string; assigned: number; resolved: number }[]
}

export interface DashboardStats {
  total_complaints: number
  pending_complaints: number
  in_progress_complaints: number
  resolved_complaints: number
  by_category: Record<ComplaintCategory, number>
  by_status: Record<ComplaintStatus, number>
  recent_complaints: Complaint[]
}

export const fetchDashboardStats = async (): Promise<DashboardStats | null> => {
  try {
    // Fetch all complaints
    const { data: complaints, error } = await supabase
      .from('complaints')
      .select(`
        *,
        user:users!complaints_user_id_fkey(full_name),
        assigned_official:users!complaints_assigned_to_fkey(full_name)
      `)
      .order('created_at', { ascending: false })

    if (error) throw error

    // Calculate statistics
    const total_complaints = complaints.length
    const pending_complaints = complaints.filter(c => c.status === 'pending').length
    const in_progress_complaints = complaints.filter(c => c.status === 'in_progress').length
    const resolved_complaints = complaints.filter(c => c.status === 'resolved').length

    // Group by category
    const by_category = complaints.reduce((acc, complaint) => {
      acc[complaint.category] = (acc[complaint.category] || 0) + 1
      return acc
    }, {} as Record<ComplaintCategory, number>)

    // Group by status
    const by_status = complaints.reduce((acc, complaint) => {
      acc[complaint.status] = (acc[complaint.status] || 0) + 1
      return acc
    }, {} as Record<ComplaintStatus, number>)

    // Get recent complaints (last 10)
    const recent_complaints = complaints.slice(0, 10)

    return {
      total_complaints,
      pending_complaints,
      in_progress_complaints,
      resolved_complaints,
      by_category,
      by_status,
      recent_complaints
    }
  } catch (error) {
    console.error('Error fetching dashboard stats:', error)
    return null
  }
}

export const fetchAnalyticsData = async (timeRange: '7d' | '30d' | '90d' | '1y' = '30d'): Promise<AnalyticsData | null> => {
  try {
    // Calculate date range based on timeRange
    const now = new Date();
    let startDate = new Date();
    
    switch (timeRange) {
      case '7d':
        startDate.setDate(now.getDate() - 7);
        break;
      case '30d':
        startDate.setDate(now.getDate() - 30);
        break;
      case '90d':
        startDate.setDate(now.getDate() - 90);
        break;
      case '1y':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        startDate.setDate(now.getDate() - 30);
    }

    // Fetch complaints within the date range
    const { data: complaints, error } = await supabase
      .from('complaints')
      .select(`
        *,
        user:users!complaints_user_id_fkey(full_name),
        assigned_official:users!complaints_assigned_to_fkey(full_name)
      `)
      .gte('created_at', startDate.toISOString())
      .order('created_at', { ascending: false });

    if (error) throw error

    // Calculate basic statistics
    const totalComplaints = complaints.length
    const resolvedComplaints = complaints.filter(c => c.status === 'resolved').length
    const pendingComplaints = complaints.filter(c => c.status === 'pending').length
    const inProgressComplaints = complaints.filter(c => c.status === 'in_progress').length

    // Calculate average resolution time
    const resolvedComplaintsList = complaints.filter(c => c.status === 'resolved' && c.created_at && c.resolved_at)
    let totalResolutionTime = 0
    resolvedComplaintsList.forEach(complaint => {
      const created = new Date(complaint.created_at)
      const resolved = new Date(complaint.resolved_at!)
      totalResolutionTime += (resolved.getTime() - created.getTime()) / (1000 * 60 * 60 * 24) // in days
    })
    const avgResolutionTime = resolvedComplaintsList.length > 0 
      ? parseFloat((totalResolutionTime / resolvedComplaintsList.length).toFixed(1)) 
      : 0

    // Category data
    const categoryCounts: Record<string, { total: number; resolved: number }> = {}
    complaints.forEach(complaint => {
      if (!categoryCounts[complaint.category]) {
        categoryCounts[complaint.category] = { total: 0, resolved: 0 }
      }
      categoryCounts[complaint.category].total++
      if (complaint.status === 'resolved') {
        categoryCounts[complaint.category].resolved++
      }
    })

    const categoryData = Object.entries(categoryCounts).map(([category, counts]) => ({
      category: category as ComplaintCategory,
      count: counts.total,
      resolved: counts.resolved
    }))

    // Monthly data (based on time range)
    const monthlyData: { month: string; complaints: number; resolved: number }[] = []
    const currentDate = new Date();
    
    // Determine the number of months to show based on time range
    let monthsToShow = 12;
    if (timeRange === '7d') monthsToShow = 1;
    else if (timeRange === '30d') monthsToShow = 2;
    else if (timeRange === '90d') monthsToShow = 4;
    
    for (let i = monthsToShow - 1; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1)
      const monthKey = date.toLocaleString('default', { month: 'short', year: 'numeric' })
      
      const monthComplaints = complaints.filter(complaint => {
        const complaintDate = new Date(complaint.created_at)
        return complaintDate.getMonth() === date.getMonth() && 
               complaintDate.getFullYear() === date.getFullYear()
      })
      
      const resolvedInMonth = monthComplaints.filter(c => c.status === 'resolved').length
      
      monthlyData.push({
        month: monthKey,
        complaints: monthComplaints.length,
        resolved: resolvedInMonth
      })
    }

    // Location data (group by location)
    const locationMap: Record<string, { lat: number; lng: number; count: number; category: ComplaintCategory }> = {}
    complaints.forEach(complaint => {
      const key = `${complaint.location_lat},${complaint.location_lng}`
      if (!locationMap[key]) {
        locationMap[key] = {
          lat: complaint.location_lat,
          lng: complaint.location_lng,
          count: 0,
          category: complaint.category
        }
      }
      locationMap[key].count++
    })

    const locationData = Object.values(locationMap)
      .sort((a, b) => b.count - a.count)
      .slice(0, 20) // Top 20 locations

    // Official data (complaints by assigned official)
    const officialMap: Record<string, { name: string; assigned: number; resolved: number }> = {}
    complaints
      .filter(c => c.assigned_to)
      .forEach(complaint => {
        const officialId = complaint.assigned_to!
        const officialName = complaint.assigned_official?.full_name || 'Unknown Official'
        
        if (!officialMap[officialId]) {
          officialMap[officialId] = {
            name: officialName,
            assigned: 0,
            resolved: 0
          }
        }
        
        officialMap[officialId].assigned++
        if (complaint.status === 'resolved') {
          officialMap[officialId].resolved++
        }
      })

    const officialData = Object.values(officialMap)
      .sort((a, b) => b.assigned - a.assigned)
      .slice(0, 10) // Top 10 officials

    return {
      totalComplaints,
      resolvedComplaints,
      pendingComplaints,
      inProgressComplaints,
      avgResolutionTime,
      categoryData,
      monthlyData,
      locationData,
      officialData
    }
  } catch (error) {
    console.error('Error fetching analytics data:', error)
    return null
  }
}

export const fetchComplaints = async (filters?: {
  status?: ComplaintStatus[]
  category?: ComplaintCategory[]
  search?: string
}): Promise<Complaint[] | null> => {
  try {
    let query = supabase
      .from('complaints')
      .select(`
        *,
        user:users!complaints_user_id_fkey(full_name),
        assigned_official:users!complaints_assigned_to_fkey(full_name)
      `)
      .order('created_at', { ascending: false })

    if (filters?.status && filters.status.length > 0) {
      query = query.in('status', filters.status)
    }

    if (filters?.category && filters.category.length > 0) {
      query = query.in('category', filters.category)
    }

    if (filters?.search) {
      query = query.ilike('title', `%${filters.search}%`)
    }

    const { data, error } = await query

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error fetching complaints:', error)
    return null
  }
}

export const updateComplaintStatus = async (
  complaintId: string, 
  status: ComplaintStatus,
  comment?: string,
  proofImageUrl?: string
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('complaints')
      .update({ 
        status,
        resolved_at: status === 'resolved' ? new Date().toISOString() : undefined
      })
      .eq('id', complaintId)

    if (error) throw error

    // Add status log
    const { error: logError } = await supabase
      .from('status_logs')
      .insert({
        complaint_id: complaintId,
        status,
        comment,
        proof_image_url: proofImageUrl,
        updated_by: supabase.auth.getUser().then(res => res.data.user?.id || '')
      })

    if (logError) console.error('Error creating status log:', logError)

    return true
  } catch (error) {
    console.error('Error updating complaint status:', error)
    return false
  }
}

export const assignComplaint = async (
  complaintId: string, 
  officialId: string
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('complaints')
      .update({ assigned_to: officialId })
      .eq('id', complaintId)

    if (error) throw error
    return true
  } catch (error) {
    console.error('Error assigning complaint:', error)
    return false
  }
}

export const fetchUsers = async (): Promise<UserType[] | null> => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error fetching users:', error)
    return null
  }
}

export const updateUserRole = async (userId: string, role: 'citizen' | 'official' | 'admin'): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('users')
      .update({ role })
      .eq('id', userId)

    if (error) throw error
    return true
  } catch (error) {
    console.error('Error updating user role:', error)
    return false
  }
}

export const deactivateUser = async (userId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('users')
      .update({ is_active: false })
      .eq('id', userId)

    if (error) throw error
    return true
  } catch (error) {
    console.error('Error deactivating user:', error)
    return false
  }
}
